import { Server } from 'lucide-react'
import { useKioskNodes, nodeFresh, type KioskNode } from '@/lib/kiosk'
import { Bar, Chip, Dot, Panel, Ring } from '@/components/kiosk/kioskUi'
import { C, fmt, pctColor } from '@/components/kiosk/kioskTheme'

function memPctOf(n: KioskNode): number | null {
  const m = n.latest_metrics
  return m?.mem_used != null && m?.mem_total ? (m.mem_used / m.mem_total) * 100 : null
}

// Offline nodes sink to the bottom; alive nodes keep declaration order.
function ordered(list: KioskNode[]): KioskNode[] {
  return [...list].sort((a, b) => Number(nodeFresh(b)) - Number(nodeFresh(a)))
}

export function NodesTile({ onTap }: { onTap: () => void }) {
  const { data: nodes } = useKioskNodes()
  const list = ordered(nodes ?? [])
  const alive = list.filter(nodeFresh).length
  return (
    <Panel
      title="Nodes"
      icon={<Server className="h-3.5 w-3.5" />}
      accent={C.cyan}
      onTap={onTap}
      right={<Chip color={alive < list.length ? C.amber : C.green}>{alive}/{list.length} en ligne</Chip>}
    >
      <div className="flex h-full flex-col justify-between">
        {list.slice(0, 5).map((n) => {
          const m = n.latest_metrics
          const cpu = m?.cpu_pct ?? 0
          const mem = memPctOf(n)
          const up = nodeFresh(n)
          return (
            <div key={n.id} className="flex items-center gap-2.5" style={{ opacity: up ? 1 : 0.42 }}>
              <Dot color={up ? C.green : C.red} pulse={!up} />
              <span className="w-[74px] shrink-0 truncate text-[14px] font-semibold" style={{ color: C.ink }}>{n.name}</span>
              {up ? (
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-7 text-[9px] uppercase" style={{ color: C.dim }}>cpu</span>
                    <Bar value={cpu} color={pctColor(cpu)} height={5} />
                    <span className="kmono w-8 shrink-0 text-right text-[11px]" style={{ color: C.ink }}>{Math.round(cpu)}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-7 text-[9px] uppercase" style={{ color: C.dim }}>mem</span>
                    <Bar value={mem ?? 0} color={pctColor(mem)} height={5} />
                    <span className="kmono w-8 shrink-0 text-right text-[11px]" style={{ color: C.ink }}>{mem != null ? `${Math.round(mem)}%` : '—'}</span>
                  </div>
                </div>
              ) : (
                <span className="flex-1 text-[12px] font-semibold uppercase tracking-wide" style={{ color: C.red }}>hors-ligne</span>
              )}
              {up && (n.gpus?.length ?? 0) > 0 && <Chip color={C.violet}>{n.gpus!.length}×GPU</Chip>}
            </div>
          )
        })}
        {list.length === 0 && <div className="flex h-full items-center justify-center text-sm" style={{ color: C.faint }}>Aucun node</div>}
      </div>
    </Panel>
  )
}

export function NodesDetail() {
  const { data: nodes } = useKioskNodes()
  const list = ordered(nodes ?? [])
  return (
    <div className="grid h-full grid-cols-3 grid-rows-2 gap-3">
      {list.slice(0, 6).map((n) => {
        const m = n.latest_metrics
        const cpu = m?.cpu_pct ?? 0
        const mem = memPctOf(n)
        const up = nodeFresh(n)
        return (
          <div key={n.id} className="kcard flex items-center gap-4 rounded-xl border px-4"
            style={{ background: C.panel, borderColor: C.line, opacity: up ? 1 : 0.5 }}>
            <Ring value={up ? cpu : 0} size={72} thickness={7} color={up ? pctColor(cpu) : C.red}>
              {up ? (
                <>
                  <span className="kmono text-lg font-bold" style={{ color: C.ink }}>{Math.round(cpu)}</span>
                  <span className="text-[9px]" style={{ color: C.dim }}>% cpu</span>
                </>
              ) : (
                <Dot color={C.red} pulse />
              )}
            </Ring>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Dot color={up ? C.green : C.red} pulse={!up} />
                <span className="truncate text-lg font-bold" style={{ color: C.ink }}>{n.name}</span>
              </div>
              {up ? (
                <>
                  <div className="mt-1 flex items-center gap-2 text-[11px]" style={{ color: C.dim }}>
                    <span className="w-7">mem</span>
                    <Bar value={mem ?? 0} max={100} color={pctColor(mem)} height={6} />
                    <span className="kmono shrink-0" style={{ color: C.ink }}>{mem != null ? `${Math.round(mem)}%` : '—'}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Chip color={C.cyan}>load {fmt(m?.load1, 1)}</Chip>
                    {(n.gpus?.length ?? 0) > 0 && <Chip color={C.violet}>{n.gpus!.length}×GPU</Chip>}
                  </div>
                </>
              ) : (
                <div className="mt-1 text-[12px] font-semibold uppercase tracking-wide" style={{ color: C.red }}>hors-ligne</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
